import { readFileSync, existsSync, mkdirSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
// Bare origin — API paths are absolute (/v1/..., /auth/cli/...), so the base must
// never carry a path prefix or auth-broker URLs would be built wrong.
export const DEFAULT_API_BASE = "https://grimoire-api.monadeo.com";
export function globalConfigPath() {
    return join(process.env.XDG_CONFIG_HOME ?? join(homedir(), ".config"), "grimoire", "config.json");
}
// Malformed config must never crash a consumer (the MCP server constructs a client
// at import time): warn and behave as if the file were absent.
function readJsonConfig(path) {
    try {
        return JSON.parse(readFileSync(path, "utf8"));
    }
    catch (err) {
        process.stderr.write(`grimoire: ignoring malformed config at ${path}: ${err.message}\n`);
        return undefined;
    }
}
// Precedence: env var > config file > default — a per-invocation env override
// (CI, QA testing) must always beat persisted settings.
export function loadGlobalConfig() {
    const path = globalConfigPath();
    const overrides = existsSync(path) ? readJsonConfig(path) : undefined;
    return {
        apiBaseUrl: DEFAULT_API_BASE,
        ...overrides,
        ...(process.env.GRIMOIRE_API_URL ? { apiBaseUrl: process.env.GRIMOIRE_API_URL } : {}),
    };
}
export function saveGlobalConfig(config) {
    const path = globalConfigPath();
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, JSON.stringify(config, null, 2));
}
// Read-modify-write against the FILE only (never the env-merged view, which
// would bake a transient GRIMOIRE_API_URL into the file). `undefined` values
// remove their key.
export function updateGlobalConfigFile(patch) {
    const path = globalConfigPath();
    const current = existsSync(path) ? (readJsonConfig(path) ?? {}) : {};
    const merged = { ...current, ...patch };
    for (const [key, value] of Object.entries(merged)) {
        if (value === undefined)
            delete merged[key];
    }
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, JSON.stringify(merged, null, 2));
    return merged;
}
// Project config (.grimoire.json in the repo root) pins a codebase's sources and
// overrides globals — committed alongside the code it documents.
export function findProjectConfig(startDir = process.cwd()) {
    let dir = startDir;
    for (;;) {
        const candidate = join(dir, ".grimoire.json");
        if (existsSync(candidate)) {
            const parsed = readJsonConfig(candidate);
            if (parsed)
                return parsed;
        }
        const parent = dirname(dir);
        if (parent === dir)
            return undefined;
        dir = parent;
    }
}
export function resolveDefaultSources() {
    return findProjectConfig()?.sources ?? loadGlobalConfig().defaultSources ?? [];
}
